import React, { useState, useContext, useRef, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  IconButton,
  Box,
  Autocomplete,
  ListItemText,
  Alert,
  Chip,
  CircularProgress,
  FormControlLabel,
  Switch,
  Grid,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Help as HelpIcon,
  TextFields as TextFieldsIcon,
  AccountTree as AccountTreeIcon,
  WhatsApp as WhatsAppIcon,
  ToggleOn as ToggleOnIcon,
} from "@mui/icons-material";
import { makeStyles } from "@material-ui/core/styles";
import { green, red } from "@material-ui/core/colors";
import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import { filterWhatsappListForCampaigns } from "../../utils/whatsappCampaignEligibility";
import { toast } from "react-toastify";

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    borderRadius: 12,
    overflow: "hidden",
    maxWidth: 760,
    width: "100%",
    maxHeight: "90vh",
    display: "flex",
    flexDirection: "column",
  },
  dialogTitle: {
    background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${
      theme.palette.primary.dark || theme.palette.primary.main
    } 100%)`,
    color: theme.palette.primary.contrastText,
    padding: theme.spacing(1.5, 2),
    display: "flex",
    alignItems: "center",
    gap: 10,
    fontSize: "1rem",
    fontWeight: 600,
  },
  dialogTitleIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 28,
    height: 28,
    borderRadius: "50%",
    backgroundColor: "rgba(255,255,255,0.2)",
  },
  dialogContent: {
    padding: theme.spacing(2, 2.5, 1.5),
    overflowY: "auto",
    flex: "1 1 auto",
    maxHeight: "calc(90vh - 120px)",
  },
  sectionTitle: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: "0.85rem",
    fontWeight: 700,
    lineHeight: 1.4,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(0.5),
  },
  sectionIcon: {
    color: theme.palette.primary.main,
    fontSize: "1.05rem !important",
    width: "1.05rem",
    height: "1.05rem",
    flexShrink: 0,
  },
  sectionDivider: {
    border: 0,
    borderTop: `1px solid ${theme.palette.divider}`,
    margin: theme.spacing(1.5, 0, 1.25),
  },
  fieldDense: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,
      fontSize: "0.875rem",
      alignItems: "center",
    },
    "& .MuiInputLabel-outlined": {
      fontSize: "0.875rem",
      lineHeight: 1.2,
      transform: "translate(14px, 12px) scale(1)",
    },
    "& .MuiInputLabel-outlined.MuiInputLabel-shrink": {
      transform: "translate(14px, -6px) scale(0.75)",
    },
    "& .MuiOutlinedInput-input": {
      padding: "12px 14px",
      boxSizing: "border-box",
      height: "auto",
      lineHeight: 1.4,
    },
    "& .MuiSelect-select": {
      padding: "12px 14px !important",
      boxSizing: "border-box",
      display: "flex",
      alignItems: "center",
      minHeight: "1.4375em",
    },
    "& .MuiAutocomplete-inputRoot": {
      paddingTop: "4px !important",
      paddingBottom: "4px !important",
      paddingLeft: "9px !important",
    },
    "& .MuiAutocomplete-input": {
      padding: "8px 5px !important",
    },
  },
  phraseList: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.25),
    width: "100%",
  },
  phraseContainer: {
    position: "relative",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 10,
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.action.hover,
    width: "100%",
    "& .MuiGrid-container": {
      alignItems: "center",
    },
  },
  addButton: {
    textTransform: "none",
    fontWeight: 600,
    borderRadius: 8,
    alignSelf: "flex-start",
    marginTop: theme.spacing(0.5),
  },
  infoAlert: {
    borderRadius: 10,
    marginBottom: theme.spacing(1.5),
  },
  whatsappChip: {
    margin: "2px",
  },
  online: {
    color: green[500],
  },
  offline: {
    color: red[500],
  },
  dialogActions: {
    padding: theme.spacing(1.25, 2),
    backgroundColor: theme.palette.grey[50],
    borderTop: `1px solid ${theme.palette.divider}`,
    gap: 8,
    flexShrink: 0,
  },
  cancelBtn: {
    textTransform: "none",
    borderRadius: 8,
    fontWeight: 600,
  },
  saveBtn: {
    textTransform: "none",
    borderRadius: 8,
    fontWeight: 600,
    minWidth: 96,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
}));

/** Mantém só IDs elegíveis e ainda presentes (lista de campanha exclui “só recebe” e conexões apagadas). */
const filterValidWhatsappIds = (ids, whatsappList) => {
  const valid = new Set((whatsappList || []).map((w) => w.id));
  return (ids || [])
    .map((id) => Number(id))
    .filter((id) => Number.isFinite(id) && valid.has(id));
};

const CampaignModalPhrase = ({ open, onClose, FlowCampaignId, onSave }) => {
  const classes = useStyles();
  const isMounted = useRef(true);
  const { user } = useContext(AuthContext);
  
  const companyId = user?.companyId;

  // Estados básicos
  const campaignEditable = true;
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(true);

  // Estados do formulário
  const [dataItem, setDataItem] = useState({
    name: "",
  });

  const [dataItemError, setDataItemError] = useState({
    name: false,
    flowId: false,
    phrases: false,
    whatsappIds: false, // NOVA VALIDAÇÃO
  });

  // Estado para múltiplas frases
  const [phrases, setPhrases] = useState([
    { text: "", type: "exact" }
  ]);

  // Estados para flows e whatsapp
  const [flowSelected, setFlowSelected] = useState(null);
  const [flowsData, setFlowsData] = useState([]);
  const [flowsDataComplete, setFlowsDataComplete] = useState([]);
  
  // ALTERAÇÃO: Estado para múltiplas conexões
  const [selectedWhatsapps, setSelectedWhatsapps] = useState([]);
  const [whatsApps, setWhatsApps] = useState([]);

  // Buscar flows
  const getFlows = async () => {
    try {
      const flows = await api.get("/flowbuilder");
      if (isMounted.current) {
        setFlowsDataComplete(flows.data.flows || []);
        setFlowsData((flows.data.flows || []).map((flow) => flow.name));
      }
      return flows.data.flows || [];
    } catch (error) {
      console.error("Erro ao buscar flows:", error);
      if (isMounted.current) {
        toast.error("Erro ao carregar fluxos");
      }
      return [];
    }
  };

  // Buscar WhatsApps (retorna lista para filtrar IDs órfãos ao editar)
  const getWhatsApps = async () => {
    try {
      const response = await api.get("/whatsapp");
      const list = filterWhatsappListForCampaigns(response.data || []);
      if (isMounted.current) {
        setWhatsApps(list);
      }
      return list;
    } catch (error) {
      console.error("Erro ao buscar WhatsApps:", error);
      if (isMounted.current) {
        toast.error("Erro ao carregar conexões");
      }
      return [];
    }
  };

  // Carregar dados se for edição (whatsappList = resposta atual de conexões, para não manter IDs apagados)
  const detailsPhrase = async (flows, whatsappList = []) => {
    if (!FlowCampaignId || !isMounted.current) return;
    
    setLoading(true);
    try {
      console.log(`Buscando dados da campanha ID: ${FlowCampaignId}`);
      const response = await api.get(`/flowcampaign/${FlowCampaignId}`);
      console.log("Resposta da API detailsPhrase:", response.data);
      
      const details = response.data;
      
      if (!isMounted.current) return;
      
      // Preencher nome da campanha
      setDataItem({
        name: details.name || "",
      });
      
      // Preencher status
      setActive(details.status !== false);
      
      let rawWhatsappIds = [];
      if (details.whatsappIds && Array.isArray(details.whatsappIds)) {
        rawWhatsappIds = details.whatsappIds;
      } else if (details.whatsappId) {
        rawWhatsappIds = [details.whatsappId];
      }
      const filteredIds = filterValidWhatsappIds(rawWhatsappIds, whatsappList);
      if (rawWhatsappIds.length && filteredIds.length < rawWhatsappIds.length) {
        toast.info(
          "Uma ou mais conexões não existem mais e foram removidas da seleção. Salve a campanha para gravar a lista atualizada."
        );
      }
      setSelectedWhatsapps(filteredIds);
      
      // Processar frases
      let parsedPhrases = [];
      try {
        if (details.phrase) {
          if (typeof details.phrase === 'string') {
            try {
              const parsed = JSON.parse(details.phrase);
              if (Array.isArray(parsed)) {
                parsedPhrases = parsed;
              } else {
                parsedPhrases = [{ text: details.phrase, type: 'exact' }];
              }
            } catch {
              parsedPhrases = [{ text: details.phrase, type: 'exact' }];
            }
          } else if (Array.isArray(details.phrase)) {
            parsedPhrases = details.phrase;
          } else if (typeof details.phrase === 'object') {
            parsedPhrases = [details.phrase];
          }
        }
        
        parsedPhrases = parsedPhrases.map(phrase => ({
          text: phrase.text || phrase,
          type: phrase.type || 'exact'
        }));
        
        if (parsedPhrases.length === 0) {
          parsedPhrases = [{ text: "", type: "exact" }];
        }
        
        console.log("Frases processadas:", parsedPhrases);
        setPhrases(parsedPhrases);
      } catch (error) {
        console.error("Erro ao processar frases:", error);
        setPhrases([{ text: "", type: "exact" }]);
      }
      
      // Selecionar flow
      if (details.flowId && flows && flows.length > 0) {
        const foundFlow = flows.find((flow) => flow.id === details.flowId);
        if (foundFlow) {
          console.log("Flow encontrado:", foundFlow.name);
          setFlowSelected(foundFlow.name);
        } else {
          console.warn("Flow não encontrado para ID:", details.flowId);
        }
      }
      
    } catch (error) {
      console.error("Erro ao carregar detalhes:", error);
      if (isMounted.current) {
        toast.error("Erro ao carregar dados da campanha");
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  };

  // Resetar estados quando modal abre/fecha
  const resetForm = () => {
    setDataItem({ name: "" });
    setPhrases([{ text: "", type: "exact" }]);
    setFlowSelected(null);
    setSelectedWhatsapps([]); // ALTERAÇÃO: Resetar array
    setActive(true);
    clearErrors();
  };

  // Inicialização
  useEffect(() => {
    isMounted.current = true;
    
    const initData = async () => {
      if (open) {
        console.log("Modal aberto. FlowCampaignId:", FlowCampaignId);
        
        resetForm();
        
        const flows = await getFlows();
        const waList = await getWhatsApps();

        if (FlowCampaignId) {
          await detailsPhrase(flows, waList);
        }
      }
    };
    
    initData();

    return () => {
      isMounted.current = false;
    };
    // resetForm / detailsPhrase são estáveis o suficiente para este ciclo open/id
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, FlowCampaignId]);

  // Função para adicionar nova frase
  const addPhraseField = () => {
    setPhrases(prevPhrases => [...prevPhrases, { text: "", type: "exact" }]);
  };

  // Função para remover frase
  const removePhraseField = (index) => {
    if (phrases.length > 1) {
      setPhrases(prevPhrases => prevPhrases.filter((_, i) => i !== index));
    }
  };

  // Função para atualizar frase específica
  const updatePhrase = (index, field, value) => {
    setPhrases(prevPhrases => {
      const newPhrases = [...prevPhrases];
      if (newPhrases[index]) {
        newPhrases[index] = { ...newPhrases[index], [field]: value };
      }
      return newPhrases;
    });
  };

  // Validação
  const validateForm = () => {
    const errors = {
      name: !dataItem.name.trim(),
      flowId: !flowSelected,
      phrases: phrases.length === 0 || phrases.some(p => !p.text.trim()),
      whatsappIds: selectedWhatsapps.length === 0 // NOVA VALIDAÇÃO
    };
    
    setDataItemError(errors);
    return !Object.values(errors).some(error => error);
  };

  // Limpar erros
  const clearErrors = () => {
    setDataItemError({
      name: false,
      flowId: false,
      phrases: false,
      whatsappIds: false,
    });
  };

  // Handler para mudanças no campo nome
  const handleNameChange = (event) => {
    const value = event.target.value;
    setDataItem(prev => ({ ...prev, name: value }));
    
    if (value.trim() && dataItemError.name) {
      setDataItemError(prev => ({ ...prev, name: false }));
    }
  };

  // NOVA FUNÇÃO: Handler para mudanças na seleção de WhatsApps
  const handleWhatsappChange = (event) => {
    const value = event.target.value;
    setSelectedWhatsapps(typeof value === 'string' ? value.split(',') : value);
    
    if (value.length > 0 && dataItemError.whatsappIds) {
      setDataItemError(prev => ({ ...prev, whatsappIds: false }));
    }
  };

  // Salvar
  const handleSave = async () => {
    if (!validateForm()) {
      toast.error("Por favor, preencha todos os campos obrigatórios");
      return;
    }

    setLoading(true);
    
    try {
      const flowIdSelected = flowsDataComplete.find(flow => flow.name === flowSelected)?.id;
      
      if (!flowIdSelected) {
        toast.error("Fluxo selecionado não encontrado");
        return;
      }

      // Filtrar frases vazias
      const validPhrases = phrases.filter(p => p.text.trim());

      const whatsappIdsClean = filterValidWhatsappIds(selectedWhatsapps, whatsApps);
      if (whatsappIdsClean.length === 0) {
        toast.error(
          "Selecione ao menos uma conexão existente. As conexões apagadas não podem ser usadas."
        );
        setLoading(false);
        return;
      }

      const payload = {
        name: dataItem.name.trim(),
        flowId: flowIdSelected,
        phrases: validPhrases,
        whatsappIds: whatsappIdsClean,
        status: active,
        ...(FlowCampaignId && { id: FlowCampaignId })
      };

      console.log("Payload para salvar:", payload);

      if (FlowCampaignId) {
        await api.put("/flowcampaign", payload);
        toast.success("Campanha atualizada com sucesso!");
      } else {
        await api.post("/flowcampaign", payload);
        toast.success("Campanha criada com sucesso!");
      }

      onSave && onSave();
      handleClose();
      
    } catch (error) {
      console.error("Erro ao salvar:", error);
      const msg =
        error.response?.data?.error ||
        error.response?.data?.message ||
        "Erro ao salvar campanha";
      toast.error(msg);
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  };

  // Fechar modal
  const handleClose = () => {
    if (!isMounted.current) return;
    
    resetForm();
    onClose();
  };

  // Verificação de segurança antes de renderizar
  if (!user || !companyId) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      scroll="paper"
      disableEscapeKeyDown={loading}
      PaperProps={{ className: classes.dialogPaper }}
    >
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <span className={classes.dialogTitleIcon}>
          <TextFieldsIcon style={{ fontSize: 18 }} />
        </span>
        {campaignEditable ? (
          <>
            {FlowCampaignId
              ? `${i18n.t("campaignsPhrase.editCampaignWithFlowByPhrase")}`
              : `${i18n.t("campaignsPhrase.newCampaignWithFlowByPhrase")}`}
          </>
        ) : (
          <>{`${i18n.t("campaigns.dialog.readonly")}`}</>
        )}
      </DialogTitle>

      <DialogContent className={classes.dialogContent} dividers={false}>
        {loading && !dataItem.name && !flowSelected ? (
          <Stack alignItems="center" justifyContent="center" minHeight={200}>
            <CircularProgress size={28} />
          </Stack>
        ) : (
          <>
            <Typography className={classes.sectionTitle}>
              <TextFieldsIcon className={classes.sectionIcon} />
              {i18n.t("campaignsPhrase.phraseTriggerName")}
            </Typography>
            <TextField
              label="Nome da campanha"
              name="name"
              variant="outlined"
              error={dataItemError.name}
              value={dataItem.name}
              margin="dense"
              onChange={handleNameChange}
              fullWidth
              className={classes.fieldDense}
              helperText={dataItemError.name ? "Nome é obrigatório" : ""}
              disabled={loading}
            />

            <hr className={classes.sectionDivider} />

            <Typography className={classes.sectionTitle}>
              <AccountTreeIcon className={classes.sectionIcon} />
              {i18n.t("campaignsPhrase.chooseAStream")}
            </Typography>
            <Autocomplete
              value={flowSelected}
              onChange={(event, newValue) => {
                setFlowSelected(newValue);
                if (newValue && dataItemError.flowId) {
                  setDataItemError((prev) => ({ ...prev, flowId: false }));
                }
              }}
              options={flowsData}
              disabled={loading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Selecione um fluxo"
                  variant="outlined"
                  margin="dense"
                  error={dataItemError.flowId}
                  helperText={dataItemError.flowId ? "Fluxo é obrigatório" : ""}
                  className={classes.fieldDense}
                />
              )}
              fullWidth
            />

            <hr className={classes.sectionDivider} />

            <Typography className={classes.sectionTitle}>
              <WhatsAppIcon className={classes.sectionIcon} />
              {i18n.t("campaignsPhrase.selectAConnection")} (Múltiplas)
            </Typography>
            <FormControl
              fullWidth
              variant="outlined"
              margin="dense"
              error={dataItemError.whatsappIds}
              className={classes.fieldDense}
            >
              <InputLabel>Conexões WhatsApp</InputLabel>
              <Select
                multiple
                value={selectedWhatsapps}
                onChange={handleWhatsappChange}
                label="Conexões WhatsApp"
                disabled={loading}
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((whatsappId) => {
                      const whatsapp = whatsApps.find((w) => w.id === whatsappId);
                      return (
                        <Chip
                          key={whatsappId}
                          label={whatsapp ? whatsapp.name : `ID: ${whatsappId}`}
                          className={classes.whatsappChip}
                          size="small"
                          color={
                            whatsapp?.status === "CONNECTED" ? "success" : "default"
                          }
                        />
                      );
                    })}
                  </Box>
                )}
              >
                {whatsApps?.length > 0 &&
                  whatsApps.map((whatsapp) => (
                    <MenuItem key={whatsapp.id} value={whatsapp.id}>
                      <Checkbox
                        checked={selectedWhatsapps.indexOf(whatsapp.id) > -1}
                      />
                      <ListItemText
                        primary={
                          <Typography component="span" style={{ fontSize: 14 }}>
                            {whatsapp.name} &nbsp;
                            <span
                              className={
                                whatsapp.status === "CONNECTED"
                                  ? classes.online
                                  : classes.offline
                              }
                            >
                              ({whatsapp.status})
                            </span>
                          </Typography>
                        }
                      />
                    </MenuItem>
                  ))}
              </Select>
              {dataItemError.whatsappIds && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ mt: 1, ml: 1.5 }}
                >
                  Pelo menos uma conexão deve ser selecionada
                </Typography>
              )}
            </FormControl>

            {selectedWhatsapps.length > 0 && (
              <Alert severity="info" className={classes.infoAlert} sx={{ mt: 1.5 }}>
                <Typography variant="body2">
                  <strong>
                    {selectedWhatsapps.length} conexão(ões) selecionada(s):
                  </strong>
                  <br />
                  A campanha será executada quando qualquer uma dessas conexões
                  receber mensagens que façam match com as frases configuradas.
                </Typography>
              </Alert>
            )}

            <hr className={classes.sectionDivider} />

            <Typography className={classes.sectionTitle}>
              <HelpIcon className={classes.sectionIcon} />
              Frases/Palavras que disparam o fluxo
            </Typography>

            <Alert severity="info" className={classes.infoAlert}>
              <Typography variant="body2">
                <strong>Correspondência Exata:</strong> A mensagem deve ser
                idêntica à frase configurada.
                <br />
                <strong>Correspondência Parcial:</strong> A frase pode estar
                contida em qualquer parte da mensagem.
              </Typography>
            </Alert>

            <div className={classes.phraseList}>
              {phrases.map((phrase, index) => (
                <Box
                  key={`phrase-${index}`}
                  className={classes.phraseContainer}
                >
                  <Grid container spacing={1} alignItems="center">
                    <Grid item xs={12} sm={phrases.length > 1 ? 7 : 8}>
                      <TextField
                        label={`Frase ${index + 1}`}
                        value={phrase.text}
                        onChange={(e) => {
                          updatePhrase(index, "text", e.target.value);
                          if (e.target.value.trim() && dataItemError.phrases) {
                            setDataItemError((prev) => ({
                              ...prev,
                              phrases: false,
                            }));
                          }
                        }}
                        variant="outlined"
                        margin="dense"
                        fullWidth
                        placeholder="Digite a frase ou palavra-chave"
                        error={dataItemError.phrases && !phrase.text.trim()}
                        helperText={
                          dataItemError.phrases && !phrase.text.trim()
                            ? "Frase é obrigatória"
                            : ""
                        }
                        disabled={loading}
                        className={classes.fieldDense}
                      />
                    </Grid>
                    <Grid item xs={phrases.length > 1 ? 9 : 12} sm={4}>
                      <FormControl
                        fullWidth
                        variant="outlined"
                        margin="dense"
                        className={classes.fieldDense}
                      >
                        <InputLabel>Tipo</InputLabel>
                        <Select
                          value={phrase.type}
                          onChange={(e) =>
                            updatePhrase(index, "type", e.target.value)
                          }
                          label="Tipo"
                          disabled={loading}
                        >
                          <MenuItem value="exact">Exata</MenuItem>
                          <MenuItem value="partial">Parcial</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    {phrases.length > 1 && (
                      <Grid item xs={3} sm={1}>
                        <IconButton
                          onClick={() => removePhraseField(index)}
                          color="error"
                          disabled={loading}
                          aria-label="Remover frase"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              ))}
            </div>

            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={addPhraseField}
              className={classes.addButton}
              disabled={loading}
              disableElevation
            >
              Adicionar Frase
            </Button>

            <hr className={classes.sectionDivider} />

            <Typography className={classes.sectionTitle}>
              <ToggleOnIcon className={classes.sectionIcon} />
              {i18n.t("campaignsPhrase.status")}
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={active}
                  onChange={() => setActive((prev) => !prev)}
                  disabled={loading}
                  color="primary"
                />
              }
              label={active ? "Status Ativa" : "Status Inativa"}
            />
          </>
        )}
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <Button
          variant="outlined"
          onClick={handleClose}
          disabled={loading}
          className={classes.cancelBtn}
        >
          {i18n.t("campaignsPhrase.cancel")}
        </Button>

        <Button
          variant="contained"
          onClick={handleSave}
          disabled={loading}
          disableElevation
          className={`${classes.btnWrapper} ${classes.saveBtn}`}
        >
          {loading ? "Salvando..." : FlowCampaignId ? "Atualizar" : "Criar"}
          {loading && (
            <CircularProgress size={24} className={classes.buttonProgress} />
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CampaignModalPhrase;